import SpaceBarOutlinedIcon from '@mui/icons-material/SpaceBarOutlined';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EditorDialogShell from 'src/common/components/modals/EditorDialogShell';
import EditorFormActions from 'src/common/components/modals/EditorFormActions';
import LightTooltip from 'src/common/components/styled/LightTooltip';
import {
  DEFAULT_DIVIDER_BLOCK_SPACING,
  hasBlockSpacing,
  normalizeBlockSpacing,
} from 'src/helpers/blockSpacing';
import { IBlockSpacing } from 'src/interfaces/PageBlock';
import BlockSpacingControls from 'src/pages/Dashboard/System/Pages/components/BlockSpacingControls';

interface Props {
  value?: IBlockSpacing;
  onChange: (value: IBlockSpacing | undefined) => void;
  isDividerBlock?: boolean;
}

export default function BlockSpacingButton({
  value,
  onChange,
  isDividerBlock = false,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<IBlockSpacing | undefined>(value);
  const active = hasBlockSpacing(value);

  useEffect(() => {
    if (open) {
      setDraft(value);
    }
  }, [open, value]);

  const handleClose = () => setOpen(false);

  const handleSave = () => {
    onChange(normalizeBlockSpacing(draft));
    setOpen(false);
  };

  const handleClear = () => {
    setDraft(isDividerBlock ? DEFAULT_DIVIDER_BLOCK_SPACING : undefined);
  };

  return (
    <>
      <LightTooltip title={t('system.pages.blockSpacing')} placement="top">
        <IconButton
          size="small"
          aria-label={t('system.pages.blockSpacing')}
          onClick={() => setOpen(true)}
          color={active ? 'primary' : 'default'}
        >
          <SpaceBarOutlinedIcon fontSize="small" />
        </IconButton>
      </LightTooltip>
      <EditorDialogShell
        id="block-spacing"
        open={open}
        onClose={handleClose}
        title={t('system.pages.blockSpacing')}
        maxWidth="sm"
      >
        <form
          onSubmit={e => {
            e.preventDefault();
            handleSave();
          }}
        >
          <BlockSpacingControls value={draft} onChange={setDraft} />
          <EditorFormActions
            justifyContent="space-between"
            leftSlot={
              <Button type="button" variant="text" onClick={handleClear}>
                {t('system.pages.blockSpacingClear')}
              </Button>
            }
            onCancel={handleClose}
            submitLabel={t('form.save')}
          />
        </form>
      </EditorDialogShell>
    </>
  );
}
